/**
 * Metrics for a strategy's uploaded history before it's created (Add strategy
 * → Past results): the Overview comparison table's metrics, built the same
 * way — the curve windowed and rebased like the Overview's growth curve
 * (strategyWindow), then summarizePerformance + computeSeriesRiskStats.
 *
 * Windows count back from the file's last day (not today), so a file that
 * ends last month still shows a full year.
 */
import { windowStrategyPoints } from './strategyWindow';
import { summarizePerformance } from './vectorCapitalScenario';
import { computeSeriesRiskStats } from './seriesRiskStats';

const BASE = 100000;

export const PREVIEW_WINDOWS = [
  { id: 'all', label: 'All time' },
  { id: '1y', label: '1 Year' },
  { id: 'ytd', label: 'YTD' },
];

// How each one is calculated (shown in the ⓘ next to its name). Every window
// starts at $100,000 on the last close before it, and "daily return" is each
// day's value ÷ the day before − 1.
export const PREVIEW_METRICS = [
  {
    key: 'totalReturnPct', label: 'Total Return', kind: 'pct',
    info: 'Ending value ÷ starting value − 1. The window starts at $100,000 on the last close before its first day.',
  },
  {
    key: 'annualizedReturnPct', label: 'Annualized Return', kind: 'pct',
    info: '(Ending value ÷ starting value) ^ (1 ÷ years) − 1, where years = calendar days from the starting close to the last day ÷ 365.25. For less than a year this projects the pace over a full year, so it can be higher than Total Return.',
  },
  {
    key: 'maxDrawdownPct', label: 'Max Drawdown', kind: 'dd',
    info: 'The largest fall from a running high (starting at $100,000) to a later low, as a % of that high.',
  },
  {
    key: 'sharpeRatio', label: 'Sharpe Ratio', kind: 'num',
    info: 'Average daily return ÷ standard deviation of daily returns × √252. Uses every day, flat days included, and no risk-free rate.',
  },
  {
    key: 'annualizedStdDevPct', label: 'Standard Deviation', kind: 'vol',
    info: 'Standard deviation of daily returns (every day, flat days included) × √252, so it reads as yearly volatility.',
  },
  {
    key: 'profitFactor', label: 'Profit Factor', kind: 'num',
    info: 'Sum of the up days’ returns ÷ the sum of the down days’ returns (as a positive number). Shown as 10 when there are no down days.',
  },
  {
    key: 'winRatePct', label: 'Win Rate', kind: 'plain',
    info: 'Up days ÷ days that moved. Flat days (no change, e.g. no trade) aren’t counted.',
  },
  {
    key: 'avgDailyReturnPct', label: 'Avg. Trade Return', kind: 'pct',
    info: 'The average daily return over the days that moved. Flat days aren’t counted, so a strategy that trades now and then isn’t diluted by its idle days.',
  },
];

export function windowStart(id, lastDate) {
  if (id === 'all' || !lastDate) return null;
  if (id === 'ytd') return `${lastDate.slice(0, 4)}-01-01`;
  const d = new Date(`${lastDate}T12:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

/** One window's metrics, or null when it has fewer than two days. */
function metricsFor(points, model, start) {
  const values = windowStrategyPoints(points, start, BASE, model, 1, null);
  if (values.length < 2) return null;
  let prev = null;
  const series = values.map((v) => {
    const dailyPnl = prev == null ? 0 : v.value - prev;
    const dailyReturnPct = prev ? (dailyPnl / prev) * 100 : 0;
    prev = v.value;
    return { date: v.date, normalized: v.value, dailyPnl, dailyReturnPct };
  });
  const summary = summarizePerformance(series, BASE);
  const risk = computeSeriesRiskStats(series);
  return {
    from: series[1]?.date || series[0].date,
    to: series[series.length - 1].date,
    totalReturnPct: summary.totalReturnPct,
    annualizedReturnPct: summary.annualizedReturnPct,
    maxDrawdownPct: summary.maxDrawdownPct,
    sharpeRatio: risk.sharpeRatio,
    annualizedStdDevPct: risk.annualizedStdDevPct,
    profitFactor: risk.profitFactor,
    winRatePct: risk.winRatePct,
    avgDailyReturnPct: risk.avgDailyReturnPct,
  };
}

/** rawPoints: [[date, equity], …] from /api/admin/strategies/preview-csv. */
export function previewMetrics(rawPoints, model = 'fixed') {
  const points = (rawPoints || [])
    .map(([date, equity]) => ({ date, equity: Number(equity) }))
    .filter((p) => p.date && Number.isFinite(p.equity))
    .sort((a, b) => a.date.localeCompare(b.date));
  const lastDate = points[points.length - 1]?.date || null;
  const out = {};
  PREVIEW_WINDOWS.forEach((w) => { out[w.id] = metricsFor(points, model, windowStart(w.id, lastDate)); });
  return { firstDate: points[0]?.date || null, lastDate, windows: out };
}

export function formatPreviewMetric(value, kind) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const v = Number(value);
  if (kind === 'num') return v.toFixed(2);
  if (kind === 'dd') return `${v === 0 ? '' : '−'}${Math.abs(v).toFixed(2)}%`;
  if (kind === 'vol' || kind === 'plain') return `${v.toFixed(2)}%`;
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}%`;
}
